import React, {useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import AttendanceStatusService from "../../../../services/dispatch/attendance-status-service.js";
import {useRequiredFields} from "../../../../hooks/use-required-fields.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const TYPE_OPTIONS = [
    {value: "OPEN", label: "Aberto"},
    {value: "CLOSE", label: "Encerrado"},
];

const initialStatus = {name: "", type: "OPEN", statusDefault: false};

const AttendanceStatusForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const [state, setState] = useState(initialStatus);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);
    const nameReference = useRef(null);
    const {clearRequiredFieldError, getRequiredFieldProps, validateRequiredFields} = useRequiredFields();

    const {isLoading: isQueryLoading, refetch} = useQuery({
        queryFn: async () => {
            const data = await AttendanceStatusService.get(id).catch(() => null);
            if (!data) {
                navigate("/404");
                return;
            }

            const {status} = data;
            setState({
                id: status.id,
                name: status.name ?? "",
                type: status.closedAttendance ? "CLOSE" : "OPEN",
                statusDefault: !!status.statusDefault,
            });
            return status;
        },
        queryKey: ["dispatch-attendance-status", id],
        enabled: !!id,
    });

    const isOpen = state.type === "OPEN";

    const onChangeType = (option) => {
        const type = option?.value ?? "OPEN";
        setState(prev => ({...prev, type, statusDefault: type === "OPEN" ? prev.statusDefault : false}));
    };

    const persist = (addAfter = false) => {
        if (!validateRequiredFields([{id: "name", value: state.name, reference: nameReference}])) return;

        const payload = {
            name: state.name,
            closedAttendance: state.type !== "OPEN",
            statusDefault: state.type === "OPEN" ? state.statusDefault : false,
        };

        const setSaving = addAfter ? setIsSavingAndAdding : setIsSaving;
        setSaving(true);
        const response = id ? AttendanceStatusService.update(id, payload) : AttendanceStatusService.save(payload);
        response
            .then((data) => {
                if (addAfter) {
                    if (id) {
                        projectUtils.success(true);
                        navigate(API_DESPACHO_WEB + "/attendance-status/form", false);
                    } else {
                        projectUtils.success(false);
                        setState(initialStatus);
                    }
                    return;
                }

                projectUtils.success(true);

                if (id) {
                    navigate(API_DESPACHO_WEB + "/attendance-status", false);
                    return;
                }

                navigate(`${API_DESPACHO_WEB}/attendance-status/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const errMessage = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir o status de atendimento "${state.name}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                AttendanceStatusService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/attendance-status", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const errMessage = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            });
    };

    const newStatus = () => navigate(API_DESPACHO_WEB + "/attendance-status/form", false);

    return (
        <Template isLoading={isSaving || isSavingAndAdding || isQueryLoading}>
            <Template.Header>
                <Template.Title>Status de atendimento</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newStatus}><FaPlus/>Adicionar status</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <InputForm
                                classNameWrapper="col-span-6"
                                inputClass={REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                id="name" label="Nome"
                                value={state.name}
                                onChange={({target: {value}}) => {
                                    setState(prev => ({...prev, name: value}));
                                    clearRequiredFieldError("name", value);
                                }}
                                reference={nameReference}
                                {...getRequiredFieldProps("name")}
                            />
                            <SelectComponent
                                classNameWrapper={`col-span-5 rounded-md ${REQUIRED_BG}`}
                                labelClass={REQUIRED_LABEl_BG}
                                label="Tipo"
                                options={TYPE_OPTIONS}
                                value={TYPE_OPTIONS.find(option => option.value === state.type) ?? null}
                                onChange={onChangeType}
                                isClearable={false}
                            />
                            {isOpen &&
                                <div className="col-span-1 flex items-center">
                                    <InputCheckbox id="statusDefault" checked={state.statusDefault}
                                                   label="Padrão"
                                                   onChange={({target}) => setState(prev => ({...prev, statusDefault: target.checked}))}/>
                                </div>
                            }
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        <Button target="primary-outline" onClick={() => persist(true)} loading={isSavingAndAdding} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist(false)} loading={isSaving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AttendanceStatusForm;
